import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useLocation, NavigateOptions } from 'react-router-dom';
import { updateSignature, ConfiguredEmailContextType } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsButton,
  WsSelect,
  ControlledTextInput,
  Item,
  WsSelectObj,
} from 'wisestep-base';
import { object, string } from 'yup';

import { SettingsHeader } from '../../../../components/SettingsHeader';
import { useEmailConfigurationContext } from '../../../../context';
import { INotification, useNotificationStore } from '../../../../utils/NotificationStore';
import WsSmtpSetup from '../../WsSmtpSetup';

import SignatureEditor from './SignatureEditor';
const CreateSignature = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const configuredContext = useEmailConfigurationContext();
  const [configuredEmails, setConfiguredEmail] = useState<ConfiguredEmailContextType[]>([]);
  const [emailData, setEmailData] = useState<ConfiguredEmailContextType>();
  const [emailsList, setEmailsList] = useState<WsSelectObj[]>([]);
  const navigate = useNavigate();
  const location: NavigateOptions = useLocation();
  const emailFrom = location.state != null && location?.state?.fromEmail;
  const signatureDefaultValues = {
    selectFromEmail: emailFrom ?? '',
    signatureBody: '',
    signatureName: '',
  };
  const validationSchema = object().shape({
    signatureName: string().required('Please enter signature name'),
    signatureBody: string().required('The body cannot be empty'),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: signatureDefaultValues,
  };
  const methods = useForm(formOptions);
  const { handleSubmit, setValue, watch, control } = methods;
  const selectedEmail = watch('selectFromEmail');
  useEffect(() => {
    (async function () {
      if (configuredContext?.configuredData != null) {
        const dataReceived = configuredContext.configuredData;
        if (dataReceived.length > 0) {
          setConfiguredEmail(dataReceived);
        }
      }
    })();
  }, [configuredContext]);
  useEffect(() => {
    const emailsArray: WsSelectObj[] = configuredEmails.map((val) => ({
      label: val.fromEmail ?? '',
      value: val.fromEmail ?? '',
    }));

    setEmailsList(emailsArray);
    const getCurrentEmailData = async () => {
      if (configuredEmails != null && selectedEmail != null) {
        const currentEmailData: ConfiguredEmailContextType[] = configuredEmails.filter(
          (data: ConfiguredEmailContextType) => {
            if (data.fromEmail === selectedEmail) return data;
          }
        );
        setEmailData(currentEmailData[0]);
        setValue('signatureBody', currentEmailData[0].signatureText);
        setValue('signatureName', currentEmailData[0].signatureName);
      }
    };
    getCurrentEmailData();
  }, [configuredEmails, selectedEmail, setValue]);

  const signatureApiCallHandler = async (data: FieldValues) => {
    if (emailData) {
      const formData: ConfiguredEmailContextType = {
        createdBy: emailData.createdBy,
        createdOn: emailData.createdOn,
        fromEmail: emailData.fromEmail,
        fromName: emailData.fromName,
        host: emailData.host,
        isDefault: emailData.isDefault,
        password: emailData.password,
        port: emailData.port,
        protection: emailData.protection,
        recruiterId: emailData.recruiterId,
        signatureName: data.signatureName,
        signatureText: data.signatureBody,
        userName: emailData.userName,
      };
      try {
        await updateSignature({ ...formData });
        const notifyObj: INotification = {
          message: 'Email Signature saved successfully.',
          type: 'success',
        };
        setNotification(notifyObj);
        navigate('/email-settings/signature');
      } catch (error) {
        const notifyObj: INotification = {
          message: 'Something went wrong, please try again.',
          type: 'warning',
        };
        setNotification(notifyObj);
      }
    }
  };

  return (
    <Box borderRadius={1.5} p={2.5}>
      <SettingsHeader title="Email Signature" />
      <Grid container spacing={2}>
        <Grid marginTop={2} item xs={2.5} pl={0} borderRadius={1.5}>
          <WsSmtpSetup />
        </Grid>
        <Grid marginTop={2} item xs={9.5}>
          <FormProvider {...methods}>
            <form>
              <Item>
                <Stack
                  justifyContent={'space-between'}
                  alignItems={'flex-start'}
                  marginTop={2}
                  direction={'row'}
                >
                  <Stack>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Signature
                    </WsTypography>
                  </Stack>

                  <Stack spacing={2} direction={'row'}>
                    <WsButton
                      onClick={handleSubmit(signatureApiCallHandler)}
                      type="submit"
                      variant="contained"
                      size="medium"
                    >
                      Save
                    </WsButton>
                    <WsButton
                      onClick={() => navigate('/email-settings/signature')}
                      variant="contained"
                      size="medium"
                    >
                      Show List
                    </WsButton>
                  </Stack>
                </Stack>
                <Box marginTop={2}>
                  <WsSelect
                    name={'selectFromEmail'}
                    label={'Select From Email:'}
                    selectArray={emailsList}
                  ></WsSelect>
                  <ControlledTextInput
                    name="signatureName"
                    labelText="Signature Name:*"
                    type="text"
                    fullWidth
                    id="outlined-basic"
                    placeholder="Eg, John Doe"
                    variant="outlined"
                    required
                  />
                  <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.BLACK}>
                    Signature Text(Paste your Signature here):
                  </WsTypography>
                  <Controller
                    render={({ field }) => (
                      <SignatureEditor
                        field={field}
                        key={`signature_editor_${emailData?.signatureText}`}
                      />
                    )}
                    name="signatureBody"
                    defaultValue={''}
                    control={control}
                  />
                </Box>
              </Item>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
    </Box>
  );
};
export default CreateSignature;
